import React, { useState, useEffect } from "react";
import { AiOutlineSave } from "react-icons/ai";
import { FaExclamationTriangle, FaTimes } from "react-icons/fa";
import { Card, CardBody, CardTitle, Input, Button, Alert, Spinner } from "reactstrap";
import "../../../styles/dashboard/SuperAdmin/CommissionSettings.css";
import { fetchCreditCosts, updateCreditCosts } from "../../../components/services/api";

const USER_ROLE = "Super Admin";

const DEFAULT_COSTS = {
  basic: "",
  // Provider-specific costs for Advanced plan
  advancedEquifax: "",
  advancedCIBIL: "",
  advancedCRIF: "",
  advancedExperian: "",
  // Provider-specific costs for Combo plan
  comboEquifax: "",
  comboCIBIL: "",
  comboCRIF: "",
  comboExperian: "",
  // Legacy fields (deprecated)
  advanced: "",
  combo: "",
  // Other costs
  panVerification: "",
  courtCaseCheck: "",
  aadhaarEkyc: "",
  itrDownload: "",
  dlVerification: "",
  rcVerification: "",
  panToBankAccount: "",
  mobileToBankAccount: "",
  mobilePrefill: "",
  panVerificationProvider: "deepvue",
  courtCaseProvider: "deepvue",
  aadhaarEkycProvider: "deepvue",
  itrDownloadProvider: "deepvue",
  dlVerificationProvider: "deepvue",
  rcVerificationProvider: "deepvue",
  panToBankAccountProvider: "idspay",
  mobileToBankAccountProvider: "idspay",
  prefillProvider: "idspay-v2",
};

export default function CommissionSettings() {
  const [costs, setCosts] = useState(DEFAULT_COSTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, color: "", message: "" });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [activeTab, setActiveTab] = useState("credit");
  const [saveNotice, setSaveNotice] = useState({ type: "", message: "" });

  useEffect(() => {
    const loadCosts = async () => {
      const result = await fetchCreditCosts();
      if (result.success) {
        const raw = result.data;
        const d = Array.isArray(raw) ? raw[0] : raw;

        if (d) {
          setCosts({
            basic: d.basicCreditCost ?? 15,
            // Provider-specific costs - Advanced
            advancedEquifax: d.advancedCreditCostEquifax ?? d.advancedCreditCost ?? 20,
            advancedCIBIL: d.advancedCreditCostCIBIL ?? 25,
            advancedCRIF: d.advancedCreditCostCRIF ?? 22,
            advancedExperian: d.advancedCreditCostExperian ?? 23,
            // Provider-specific costs - Combo
            comboEquifax: d.comboCreditCostEquifax ?? d.comboCreditCost ?? 35,
            comboCIBIL: d.comboCreditCostCIBIL ?? 40,
            comboCRIF: d.comboCreditCostCRIF ?? 38,
            comboExperian: d.comboCreditCostExperian ?? 39,
            // Legacy
            advanced: d.advancedCreditCost ?? 25,
            combo: d.comboCreditCost ?? 38,
            // Other costs
            panVerification: d.panVerificationCost ?? 5,
            courtCaseCheck: d.courtCaseCheckCost ?? 10,
            aadhaarEkyc: d.aadhaarEkycCost ?? 10,
            itrDownload: d.itrDownloadCost ?? 10,
            dlVerification: d.dlVerificationCost ?? 10,
            rcVerification: d.rcVerificationCost ?? 10,
            panToBankAccount: d.panToBankAccountCost ?? 10,
            mobileToBankAccount: d.mobileToBankAccountCost ?? 10,
            mobilePrefill: d.mobilePrefillCost ?? 10,
            panVerificationProvider: d.panVerificationProvider ?? "deepvue",
            courtCaseProvider: d.courtCaseProvider ?? "deepvue",
            aadhaarEkycProvider: d.aadhaarEkycProvider ?? "deepvue",
            itrDownloadProvider: d.itrDownloadProvider ?? "deepvue",
            dlVerificationProvider: d.dlVerificationProvider ?? "deepvue",
            rcVerificationProvider: d.rcVerificationProvider ?? "deepvue",
            panToBankAccountProvider: d.panToBankAccountProvider ?? "idspay",
            mobileToBankAccountProvider: d.mobileToBankAccountProvider ?? "idspay",
            prefillProvider: d.prefillProvider ?? "idspay-v2",
          });
        } else {
          setCosts({
            basic: 15,
            // Provider-specific defaults - Advanced
            advancedEquifax: 20,
            advancedCIBIL: 25,
            advancedCRIF: 22,
            advancedExperian: 23,
            // Provider-specific defaults - Combo
            comboEquifax: 35,
            comboCIBIL: 40,
            comboCRIF: 38,
            comboExperian: 39,
            // Legacy
            advanced: 25,
            combo: 38,
            // Other costs
            panVerification: 5,
            courtCaseCheck: 10,
            aadhaarEkyc: 10,
            itrDownload: 10,
            dlVerification: 10,
            rcVerification: 10,
            panToBankAccount: 10,
            mobileToBankAccount: 10,
            mobilePrefill: 10,
            panVerificationProvider: "deepvue",
            courtCaseProvider: "deepvue",
            aadhaarEkycProvider: "deepvue",
            itrDownloadProvider: "deepvue",
            dlVerificationProvider: "deepvue",
            rcVerificationProvider: "deepvue",
            panToBankAccountProvider: "idspay",
            mobileToBankAccountProvider: "idspay",
            prefillProvider: "idspay-v2",
          });
          showAlert("warning", "No credit cost data found. Using default values.");
        }
      } else {
        showAlert("danger", result.error);
      }
      setLoading(false);
    };
    loadCosts();
  }, []);

  const handleChange = (key, value) => {
    setCosts((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveClick = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmSave = async () => {
    setShowConfirmModal(false);
    setSaving(true);
    const payload = {
      basicCreditCost: parseFloat(costs.basic) || 0,
      // Provider-specific costs - Advanced
      advancedCreditCostEquifax: parseFloat(costs.advancedEquifax) || 0,
      advancedCreditCostCIBIL: parseFloat(costs.advancedCIBIL) || 0,
      advancedCreditCostCRIF: parseFloat(costs.advancedCRIF) || 0,
      advancedCreditCostExperian: parseFloat(costs.advancedExperian) || 0,
      // Provider-specific costs - Combo
      comboCreditCostEquifax: parseFloat(costs.comboEquifax) || 0,
      comboCreditCostCIBIL: parseFloat(costs.comboCIBIL) || 0,
      comboCreditCostCRIF: parseFloat(costs.comboCRIF) || 0,
      comboCreditCostExperian: parseFloat(costs.comboExperian) || 0,
      // Legacy (backward compatibility)
      advancedCreditCost: parseFloat(costs.advanced) || 0,
      comboCreditCost: parseFloat(costs.combo) || 0,
      // Other costs
      panVerificationCost: parseFloat(costs.panVerification) || 0,
      courtCaseCheckCost: parseFloat(costs.courtCaseCheck) || 0,
      aadhaarEkycCost: parseFloat(costs.aadhaarEkyc) || 0,
      itrDownloadCost: parseFloat(costs.itrDownload) || 0,
      dlVerificationCost: parseFloat(costs.dlVerification) || 0,
      rcVerificationCost: parseFloat(costs.rcVerification) || 0,
      panToBankAccountCost: parseFloat(costs.panToBankAccount) || 0,
      mobileToBankAccountCost: parseFloat(costs.mobileToBankAccount) || 0,
      mobilePrefillCost: parseFloat(costs.mobilePrefill) || 0,
      panVerificationProvider: costs.panVerificationProvider || "deepvue",
      courtCaseProvider: costs.courtCaseProvider || "deepvue",
      aadhaarEkycProvider: costs.aadhaarEkycProvider || "deepvue",
      itrDownloadProvider: costs.itrDownloadProvider || "deepvue",
      dlVerificationProvider: costs.dlVerificationProvider || "deepvue",
      rcVerificationProvider: costs.rcVerificationProvider || "deepvue",
      panToBankAccountProvider: costs.panToBankAccountProvider || "idspay",
      mobileToBankAccountProvider: costs.mobileToBankAccountProvider || "idspay",
      prefillProvider: costs.prefillProvider || "idspay-v2",
    };

    const result = await updateCreditCosts(payload);
    setSaving(false);

    if (result.success) {
      showAlert("success", "Settings saved successfully!");
      setSaveNotice({ type: "success", message: "Settings saved successfully." });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      showAlert("danger", result.error);
      setSaveNotice({ type: "error", message: result.error || "Failed to save settings." });
    }
    setTimeout(() => setSaveNotice({ type: "", message: "" }), 4000);
  };

  const handleCancelSave = () => {
    setShowConfirmModal(false);
  };

  const showAlert = (color, message) => {
    setAlert({ show: true, color, message });
    setTimeout(() => setAlert({ show: false, color: "", message: "" }), 4000);
  };

  const isReadOnly = USER_ROLE !== "Super Admin";

  const renderInput = (label, key, hint) => (
    <div className="ccs-option-row">
      <div className="ccs-option-header">
        <div className="ccs-option-title">{label}</div>
        <span className="ccs-option-badge">Cost</span>
      </div>
      <div className="ccs-field ccs-field-compact">
        <div className="ccs-input-group">
          <span className="ccs-input-prefix">₹</span>
          <Input
            type="number"
            value={costs[key]}
            onChange={(e) => handleChange(key, e.target.value)}
            className="ccs-input ccs-input-inline"
            placeholder="e.g. 10.00"
            inputMode="decimal"
            disabled={isReadOnly || saving}
          />
        </div>
        <small className="text-muted">{hint}</small>
      </div>
    </div>
  );

  const renderServiceBlock = (title, costKey, costHint) => (
    <div className="ccs-service-card">
      <div className="ccs-service-title">{title}</div>
      <div className="ccs-service-grid">
        {renderInput("Cost", costKey, costHint)}
      </div>
    </div>
  );

  const renderProviderSelect = (label, key, hint, providers = ["deepvue"]) => (
    <div className="ccs-option-row">
      <div className="ccs-option-header">
        <div className="ccs-option-title">{label}</div>
        <span className="ccs-option-badge ccs-option-badge-provider">Provider</span>
      </div>
      <div className="ccs-field ccs-field-compact">
        <div className="ccs-select-wrapper">
          <Input
            type="select"
            value={costs[key]}
            onChange={(e) => handleChange(key, e.target.value)}
            className="ccs-input ccs-input-inline ccs-select"
            disabled={isReadOnly || saving}
          >
            {providers.map((provider) => {
              const value = typeof provider === "string" ? provider : provider.value;
              const text = typeof provider === "string" ? provider : provider.label;
              return <option key={value} value={value}>{text}</option>;
            })}
          </Input>
        </div>
        <small className="text-muted">{hint}</small>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="ccs-container ccs-loading-container d-flex justify-content-center align-items-center">
        <Spinner color="primary" /> <span className="ms-2">Loading settings...</span>
      </div>
    );
  }

  return (
    <div className="ccs-container">
      {alert.show && (
        <Alert color={alert.color} className="ccs-alert">
          {alert.message}
        </Alert>
      )}

      {saveNotice.message && (
        <div className={`ccs-save-notice ccs-save-notice-top ${saveNotice.type}`}>
          {saveNotice.message}
        </div>
      )}

      <Card className="ccs-card shadow-sm border-0">
        <CardBody className="p-4">
          <CardTitle tag="h4" className="ccs-title mb-4">
            Commission Settings
          </CardTitle>

          <div className="ccs-tabs">
            <button
              type="button"
              className={`ccs-tab-btn ${activeTab === "credit" ? "active" : ""}`}
              onClick={() => setActiveTab("credit")}
            >
              Credit Score Check
            </button>
            <button
              type="button"
              className={`ccs-tab-btn ${activeTab === "kyc" ? "active" : ""}`}
              onClick={() => setActiveTab("kyc")}
            >
              KYC Verification
            </button>
            <button
              type="button"
              className={`ccs-tab-btn ${activeTab === "api" ? "active" : ""}`}
              onClick={() => setActiveTab("api")}
            >
              API Settings
            </button>
          </div>

          <div className="ccs-form">
            <div className="ccs-grid">
              {activeTab === "credit" && (
                <>
                  {renderServiceBlock(
                    "Basic Credit",
                    "basic",
                    "Per credit cost for Basic plan (same for all modes)"
                  )}
                  <div className="ccs-divider">
                    <h5>Advanced Plan - Provider-Specific Costs</h5>
                  </div>
                  {renderServiceBlock(
                    "Advanced - Equifax (With OTP)",
                    "advancedEquifax",
                    "Per credit cost for Advanced plan using Equifax"
                  )}
                  {renderServiceBlock(
                    "Advanced - CIBIL (Without OTP)",
                    "advancedCIBIL",
                    "Per credit cost for Advanced plan using TransUnion CIBIL"
                  )}
                  {renderServiceBlock(
                    "Advanced - CRIF (Without OTP)",
                    "advancedCRIF",
                    "Per credit cost for Advanced plan using CRIF Highmark"
                  )}
                  {renderServiceBlock(
                    "Advanced - Experian (Without OTP)",
                    "advancedExperian",
                    "Per credit cost for Advanced plan using Experian"
                  )}
                  <div className="ccs-divider">
                    <h5>Combo Plan - Provider-Specific Costs</h5>
                  </div>
                  {renderServiceBlock(
                    "Combo - Equifax (With OTP)",
                    "comboEquifax",
                    "Per credit cost for Combo plan using Equifax"
                  )}
                  {renderServiceBlock(
                    "Combo - CIBIL (Without OTP)",
                    "comboCIBIL",
                    "Per credit cost for Combo plan using TransUnion CIBIL"
                  )}
                  {renderServiceBlock(
                    "Combo - CRIF (Without OTP)",
                    "comboCRIF",
                    "Per credit cost for Combo plan using CRIF Highmark"
                  )}
                  {renderServiceBlock(
                    "Combo - Experian (Without OTP)",
                    "comboExperian",
                    "Per credit cost for Combo plan using Experian"
                  )}
                  <div className="ccs-divider">
                    <h5>Mobile Prefill</h5>
                  </div>
                  {renderServiceBlock(
                    "Mobile Prefill Cost",
                    "mobilePrefill",
                    "Per lookup cost for Mobile-to-Prefill (Advanced & Combo plans only)"
                  )}
                </>
              )}

              {activeTab === "kyc" && (
                <>
                  {renderInput(
                    "PAN Verification",
                    "panVerification",
                    "Per verification cost for PAN service"
                  )}
                  {renderInput(
                    "Court Case Check",
                    "courtCaseCheck",
                    "Per check cost for Court Case service"
                  )}
                  {renderInput(
                    "DL Verification",
                    "dlVerification",
                    "Per request cost for DL verification"
                  )}
                  {renderInput(
                    "RC Verification",
                    "rcVerification",
                    "Per request cost for RC verification"
                  )}
                  {renderInput(
                    "PAN To Bank Account",
                    "panToBankAccount",
                    "Per request cost for PAN to bank account verification"
                  )}
                  {renderInput(
                    "Mobile To Bank Account",
                    "mobileToBankAccount",
                    "Per request cost for mobile to bank account verification"
                  )}
                </>
              )}

              {activeTab === "api" && (
                <>
                  {renderProviderSelect(
                    "PAN Provider",
                    "panVerificationProvider",
                    "Select API provider for PAN verification"
                  )}
                  {renderProviderSelect(
                    "Court Case Provider",
                    "courtCaseProvider",
                    "Select API provider for court case checks"
                  )}
                  {renderProviderSelect(
                    "DL Verification Provider",
                    "dlVerificationProvider",
                    "Select API provider for DL verification"
                  )}
                  {renderProviderSelect(
                    "RC Verification Provider",
                    "rcVerificationProvider",
                    "Select API provider for RC verification"
                  )}
                  {renderProviderSelect(
                    "PAN To Bank Account Provider",
                    "panToBankAccountProvider",
                    "Select API provider for PAN to bank account verification",
                    ["idspay"]
                  )}
                  {renderProviderSelect(
                    "Mobile To Bank Account Provider",
                    "mobileToBankAccountProvider",
                    "Select API provider for mobile to bank account verification",
                    ["idspay"]
                  )}
                  {renderProviderSelect(
                    "Prefill Provider",
                    "prefillProvider",
                    "Select which IDS Pay API is used for Mobile-to-Prefill lookups",
                    [
                      { value: "idspay-v1", label: "Idspay-mobile-to-prefil" },
                      { value: "idspay-v2", label: "Idspay-prefil-v2" },
                    ]
                  )}
                </>
              )}
            </div>

            {!isReadOnly && (
              <Button
                color="primary"
                onClick={handleSaveClick}
                disabled={saving}
                className="ccs-save-btn"
              >
                {saving ? (
                  <>
                    <Spinner size="sm" className="me-2" /> Saving...
                  </>
                ) : (
                  <>
                    <AiOutlineSave className="me-2" /> Save Settings
                  </>
                )}
              </Button>
            )}


            {isReadOnly && (
              <p className="text-muted mt-3">
                <em>You do not have permission to edit these settings.</em>
              </p>
            )}
          </div>
        </CardBody>
      </Card>

      {showConfirmModal && (
        <div className="ccs-modal-overlay" onClick={handleCancelSave}>
          <div className="ccs-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="ccs-modal-header">
              <div className="ccs-warning-icon">
                <FaExclamationTriangle />
              </div>
              <h3>Confirm Commission Changes</h3>
              <button className="ccs-close-btn" onClick={handleCancelSave}>
                <FaTimes />
              </button>
            </div>
            <div className="ccs-modal-body">
              <p className="ccs-warning-text">
                <strong>Warning:</strong> You are about to change the commission settings. This will affect:
              </p>
              <ul className="ccs-impact-list">
                <li>All future credit purchases by organizations</li>
                <li>Pricing for all active and new users</li>
                <li>Financial calculations across the entire system</li>
              </ul>
              {activeTab !== "api" && (
                <div className="ccs-cost-summary">
                  <h4>New Cost Structure:</h4>
                  <div className="ccs-summary-grid">
                    <div className="ccs-summary-section">
                      <div className="ccs-summary-title">Credit Score Check</div>
                      <div className="ccs-summary-row"><span>Basic Credit:</span><strong>₹{costs.basic}</strong></div>
                      <div className="ccs-summary-row"><span>Advanced (Equifax):</span><strong>₹{costs.advancedEquifax}</strong></div>
                      <div className="ccs-summary-row"><span>Advanced (CIBIL):</span><strong>₹{costs.advancedCIBIL}</strong></div>
                      <div className="ccs-summary-row"><span>Advanced (CRIF):</span><strong>₹{costs.advancedCRIF}</strong></div>
                      <div className="ccs-summary-row"><span>Advanced (Experian):</span><strong>₹{costs.advancedExperian}</strong></div>
                      <div className="ccs-summary-row"><span>Combo (Equifax):</span><strong>₹{costs.comboEquifax}</strong></div>
                      <div className="ccs-summary-row"><span>Combo (CIBIL):</span><strong>₹{costs.comboCIBIL}</strong></div>
                      <div className="ccs-summary-row"><span>Combo (CRIF):</span><strong>₹{costs.comboCRIF}</strong></div>
                      <div className="ccs-summary-row"><span>Combo (Experian):</span><strong>₹{costs.comboExperian}</strong></div>
                      <div className="ccs-summary-row"><span>Mobile Prefill:</span><strong>₹{costs.mobilePrefill}</strong></div>
                    </div>
                    <div className="ccs-summary-section">
                      <div className="ccs-summary-title">KYC Verification</div>
                      <div className="ccs-summary-row"><span>PAN Verification:</span><strong>₹{costs.panVerification}</strong></div>
                      <div className="ccs-summary-row"><span>Court Case Check:</span><strong>₹{costs.courtCaseCheck}</strong></div>
                      <div className="ccs-summary-row"><span>DL Verification:</span><strong>₹{costs.dlVerification}</strong></div>
                      <div className="ccs-summary-row"><span>RC Verification:</span><strong>₹{costs.rcVerification}</strong></div>
                      <div className="ccs-summary-row"><span>PAN To Bank Account:</span><strong>₹{costs.panToBankAccount}</strong></div>
                      <div className="ccs-summary-row"><span>Mobile To Bank Account:</span><strong>₹{costs.mobileToBankAccount}</strong></div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "api" && (
                <div className="ccs-cost-summary">
                  <h4>API Provider Changes:</h4>
                  <div className="ccs-summary-grid">
                    <div className="ccs-summary-section">
                      <div className="ccs-summary-title">KYC Providers</div>
                      <div className="ccs-summary-row"><span>PAN Provider:</span><strong>{costs.panVerificationProvider}</strong></div>
                      <div className="ccs-summary-row"><span>Court Case Provider:</span><strong>{costs.courtCaseProvider}</strong></div>
                      <div className="ccs-summary-row"><span>DL Verification Provider:</span><strong>{costs.dlVerificationProvider}</strong></div>
                      <div className="ccs-summary-row"><span>RC Verification Provider:</span><strong>{costs.rcVerificationProvider}</strong></div>
                      <div className="ccs-summary-row"><span>PAN To Bank Account Provider:</span><strong>{costs.panToBankAccountProvider}</strong></div>
                      <div className="ccs-summary-row"><span>Mobile To Bank Account Provider:</span><strong>{costs.mobileToBankAccountProvider}</strong></div>
                      <div className="ccs-summary-row"><span>Mobile Prefill Provider:</span><strong>{costs.prefillProvider}</strong></div>
                    </div>
                  </div>
                </div>
              )}
              <p className="ccs-confirm-text">
                Are you sure you want to proceed with these changes?
              </p>
            </div>
            <div className="ccs-modal-footer">
              <button className="ccs-cancel-btn" onClick={handleCancelSave}>
                Cancel
              </button>
              <button className="ccs-confirm-btn" onClick={handleConfirmSave}>
                Confirm Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
